import React, { useState, useEffect, useMemo } from "react";
import { Modal, Button, Tag, Empty, message, InputNumber } from "antd";
import debounce from "lodash/debounce";
import { getAggregatorCatalogueProducts } from "../../services/index";
import { isItemOutOfStock, isItemPartialStock } from "../../utils/aggregatorStockCheck";
import useAuthStore from "../../stores/useAuthStore";
import { formatCurrencyAmount } from "../../utils/tillHelpers";

const formatCurrency = (amount) => formatCurrencyAmount(useAuthStore.getState().tillData, amount);

// Balance is display-only reconciliation against the backend's own zero-sum
// check (which is gross/tax-aware and the real gate) — this tolerance is just
// so a harmless floating-point remainder doesn't block Save Changes.
const BALANCE_TOLERANCE = 0.01;

// The backend's freed/allocated invariant is GROSS (see AggregatorNewLineInput's
// overrideUnitPrice doc: "gross per unit"), but item.price on this order shape
// is netunit (tax-exclusive, see salesHistoryUtils' lineItems mapping). Mixing
// the two would let this preview show "balanced" for a payload the backend's
// own gross-based check rejects. linenet + tax, divided by the ORIGINAL
// quantity, approximates the same per-unit gross the backend already stores as
// grossunit — close enough for this preview, which is display-only regardless.
const grossUnitPrice = (item) => {
  const qty = item?.quantity || 0;
  if (qty > 0 && (item.linenet != null || item.tax != null)) {
    return (Number(item.linenet || 0) + Number(item.tax || 0)) / qty;
  }
  return Number(item?.price) || 0;
};

// A line can give its value up to fund substitutes (a Replace that has
// substitutes attached) only if it's undiscounted -- the backend refuses to
// REMOVE a discounted line -- and actually worth something.
const isFundableLine = (item) => Number(item?.discount || 0) === 0 && grossUnitPrice(item) * (item?.quantity || 0) > 0;

// TSK-3229 / TSK-3793 / TSK-4127: single-step item modification for AK/FR
// aggregator orders. Two internal views mirror the solution doc's wireframes —
// "replace" (item list, per-line actions) and "confirm" (change summary +
// financial impact) — both submitted in one posAggregatorItemModify call, no
// draft/approval step.
//
// Each order line carries at most one edit in `lineEdits`: either REPLACE
// (swap the product, TSK-3229/3793) or QTY_EDIT (cut qty back to what's in
// stock, TSK-4127) — never both, matching the backend's per-line mutual
// exclusion. `newLines` is a separate, order-level list of substitute items
// that spend whatever QTY_EDIT frees; it's not tied 1:1 to any single line
// (sourceLineId in the mutation is audit-only), so it lives outside lineEdits.
const ModifyItemsModal = ({ visible, order, stockMap, substitutedLineIds, tillId, onClose, onConfirm, confirming }) => {
  const [view, setView] = useState("replace");
  const [lineEdits, setLineEdits] = useState({});
  const [newLines, setNewLines] = useState([]);

  // Per-line Replace search.
  const [activeIndex, setActiveIndex] = useState(null);
  const [searchValue, setSearchValue] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  // Per-line Edit Qty.
  const [qtyEditIndex, setQtyEditIndex] = useState(null);
  const [qtyDraft, setQtyDraft] = useState(null);

  // Add Substitute Item.
  const [addingNewLine, setAddingNewLine] = useState(false);
  const [newLineSearchValue, setNewLineSearchValue] = useState("");
  const [newLineSearchResults, setNewLineSearchResults] = useState([]);
  const [newLineSearching, setNewLineSearching] = useState(false);
  const [pendingNewLineProduct, setPendingNewLineProduct] = useState(null);
  const [pendingNewLineQty, setPendingNewLineQty] = useState(1);

  const items = order?.items || [];

  const resetAll = () => {
    setView("replace");
    setLineEdits({});
    setNewLines([]);
    setActiveIndex(null);
    setSearchValue("");
    setSearchResults([]);
    setQtyEditIndex(null);
    setQtyDraft(null);
    setAddingNewLine(false);
    setNewLineSearchValue("");
    setNewLineSearchResults([]);
    setPendingNewLineProduct(null);
    setPendingNewLineQty(1);
  };

  useEffect(() => {
    if (!visible) resetAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  // BUG-2406: edits are keyed by line index, so drafts made on one order must
  // never carry over onto another order's lines if the order changes underneath.
  useEffect(() => {
    resetAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order?.sOrderID]);

  const runSearch = useMemo(
    () =>
      debounce(async (term) => {
        if (!term || term.trim().length < 2) {
          setSearchResults([]);
          setSearching(false);
          return;
        }
        try {
          const response = await getAggregatorCatalogueProducts(tillId, term.trim());
          const results = response?.data?.data?.getAggregatorCatalogueProducts || [];
          setSearchResults(results);
        } catch (error) {
          console.error("Error searching catalogue products:", error);
          setSearchResults([]);
        } finally {
          setSearching(false);
        }
      }, 300),
    [tillId]
  );

  const runNewLineSearch = useMemo(
    () =>
      debounce(async (term) => {
        if (!term || term.trim().length < 2) {
          setNewLineSearchResults([]);
          setNewLineSearching(false);
          return;
        }
        try {
          const response = await getAggregatorCatalogueProducts(tillId, term.trim());
          const results = response?.data?.data?.getAggregatorCatalogueProducts || [];
          setNewLineSearchResults(results);
        } catch (error) {
          console.error("Error searching catalogue products:", error);
          setNewLineSearchResults([]);
        } finally {
          setNewLineSearching(false);
        }
      }, 300),
    [tillId]
  );

  useEffect(
    () => () => {
      runSearch.cancel();
      runNewLineSearch.cancel();
    },
    [runSearch, runNewLineSearch]
  );

  // ── Replace ────────────────────────────────────────────────────────────
  const handleReplaceClick = (index) => {
    setQtyEditIndex(null);
    setActiveIndex(index);
    setSearchValue("");
    setSearchResults([]);
  };

  const handleSearchChange = (value) => {
    setSearchValue(value);
    setSearching(!!value && value.trim().length >= 2);
    runSearch(value);
  };

  const handleSelectResult = (index, product) => {
    setLineEdits((prev) => ({ ...prev, [index]: { type: "REPLACE", product } }));
    setActiveIndex(null);
  };

  // ── Edit Qty (TSK-4127) ───────────────────────────────────────────────
  const handleEditQtyClick = (index) => {
    setActiveIndex(null);
    const item = items[index];
    const stockEntry = stockMap?.[item.sOrderlineID];
    const availableQty = stockEntry?.sellableQty ?? stockEntry?.onhandQty;
    const oldQty = item.quantity || 0;
    // Prefill with what's actually available when that's the constraint,
    // otherwise leave room for the operator to pick.
    const suggestion = availableQty != null && availableQty >= 1 && availableQty < oldQty ? Math.floor(availableQty) : Math.max(1, oldQty - 1);
    setQtyDraft(suggestion);
    setQtyEditIndex(index);
  };

  const handleApplyQtyEdit = (index) => {
    const item = items[index];
    const oldQty = item.quantity || 0;
    const stockEntry = stockMap?.[item.sOrderlineID];
    const availableQty = stockEntry?.sellableQty ?? stockEntry?.onhandQty;
    const newQty = Number(qtyDraft);

    if (!Number.isFinite(newQty) || newQty < 1) {
      message.error("Enter a quantity of at least 1.");
      return;
    }
    if (newQty >= oldQty) {
      message.error(`The reduced quantity must be less than the ordered quantity (${oldQty}).`);
      return;
    }
    if (availableQty != null && newQty > availableQty) {
      message.error(`Only ${availableQty} in stock — cannot keep ${newQty}.`);
      return;
    }
    setLineEdits((prev) => ({ ...prev, [index]: { type: "QTY_EDIT", newQty } }));
    setQtyEditIndex(null);
  };

  // ── Split (TIC-10595) ────────────────────────────────────────────────
  // A line already replaced in an earlier save still carries its original
  // value (a plain REPLACE never moves money), but nothing in a later session
  // could free it: Edit Qty needs qty > 1 and Replace hides the line's own
  // product. Split frees the whole line (sent as REMOVE) and keeps its current
  // product as the first substitute, so more items can share that value.
  const handleSplitClick = (index) => {
    setActiveIndex(null);
    setQtyEditIndex(null);
    setLineEdits((prev) => ({ ...prev, [index]: { type: "SPLIT" } }));
  };

  const handleChangeEdit = (index) => {
    setLineEdits((prev) => {
      const next = { ...prev };
      delete next[index];
      return next;
    });
  };

  // ── Add Substitute Item ──────────────────────────────────────────────
  const handleAddSubstituteClick = () => {
    setAddingNewLine(true);
    setNewLineSearchValue("");
    setNewLineSearchResults([]);
    setPendingNewLineProduct(null);
    setPendingNewLineQty(1);
  };

  const handleNewLineSearchChange = (value) => {
    setNewLineSearchValue(value);
    setNewLineSearching(!!value && value.trim().length >= 2);
    runNewLineSearch(value);
  };

  const handlePickNewLineProduct = (product) => {
    setPendingNewLineProduct(product);
    setPendingNewLineQty(1);
  };

  // Price is always auto-derived from the freed amount (Sudheer's note:
  // "newItemOverridePrice = freedAmount / newItemQty") -- never asked of the
  // operator, since a substitute's own catalogue price would almost never
  // match what was actually freed and would just trip the balance check.
  const handleConfirmAddNewLine = () => {
    const qty = Number(pendingNewLineQty);
    if (!pendingNewLineProduct) return;
    if (!Number.isFinite(qty) || qty < 1) {
      message.error("Enter a quantity of at least 1.");
      return;
    }
    setNewLines((prev) => [
      ...prev,
      { key: `${pendingNewLineProduct.mProductId}-${Date.now()}`, product: pendingNewLineProduct, qty },
    ]);
    setAddingNewLine(false);
    setPendingNewLineProduct(null);
  };

  const handleRemoveNewLine = (key) => setNewLines((prev) => prev.filter((nl) => nl.key !== key));

  // ── Freed / allocated balance (preview only — the mutation's own zero-sum
  // check, which works in gross and per-substitute tax, is the real gate) ──
  // Edit Qty frees money into the shared pool that substitutes spend from
  // (backend: "any mix of REMOVE + QTY_EDIT funding any number of new items").
  const hasExplicitFunding = Object.values(lineEdits).some((edit) => edit.type === "QTY_EDIT");
  const hasNewLines = newLines.length > 0;

  // A plain REPLACE frees nothing (the backend keeps qty/price/tax as-is), so
  // on its own it can't fund a substitute. When substitutes are added after a
  // Replace and nothing else in the order is funding them, the replaced line
  // is sent as REMOVE and its replacement product becomes the first
  // substitute (at the line's ordered qty) -- the freed amount is then shared
  // across the replacement and the substitutes. A Replace with no
  // substitutes is untouched: still a plain 1:1 swap.
  const fundingReplaceIndexes =
    hasNewLines && !hasExplicitFunding
      ? Object.entries(lineEdits)
          .filter(([index, edit]) => edit.type === "REPLACE" && isFundableLine(items[index]))
          .map(([index]) => index)
      : [];
  // TIC-10595: lines replaced in an earlier save that can fund a Split (their
  // value is still the original line's -- a plain REPLACE never moves money).
  const isSplitCandidate = (item) => !!substitutedLineIds?.has(item.sOrderlineID) && !!item.productId && isFundableLine(item);
  const splitCandidateCount = items.filter(isSplitCandidate).length;
  const splitCandidateIndexes = items.map((item, index) => String(index)).filter((index) => !lineEdits[index] && isSplitCandidate(items[index]));
  // "+ Add Substitute Item" alone is enough when exactly one such line exists
  // and nothing else in this session funds the substitutes: that line is Split
  // automatically. With several candidates the operator picks via Split.
  const autoSplitIndex =
    hasNewLines && !hasExplicitFunding && fundingReplaceIndexes.length === 0 && !Object.values(lineEdits).some((edit) => edit.type === "SPLIT") && splitCandidateCount === 1 && splitCandidateIndexes.length === 1
      ? splitCandidateIndexes[0]
      : null;
  // lineEdits plus the automatic Split, used for everything computed or sent below.
  const effectiveEdits = autoSplitIndex != null ? { ...lineEdits, [autoSplitIndex]: { type: "SPLIT", auto: true } } : lineEdits;

  // A Split always funds (it's only ever chosen to add substitutes), unlike a
  // Replace, which stays a plain 1:1 swap unless substitutes need funding.
  const splitIndexes = Object.entries(effectiveEdits)
    .filter(([, edit]) => edit.type === "SPLIT")
    .map(([index]) => index);
  const hasFundingEdit = hasExplicitFunding || fundingReplaceIndexes.length > 0 || splitIndexes.length > 0;

  const totalFreed = Object.entries(effectiveEdits).reduce((sum, [index, edit]) => {
    const item = items[index];
    if (edit.type === "QTY_EDIT") {
      const diffQty = (item.quantity || 0) - (edit.newQty || 0);
      return sum + diffQty * grossUnitPrice(item);
    }
    // A Replace that's funding substitutes (or a Split) gives up the line's
    // whole value (sent as REMOVE), which works even for qty-1 lines that
    // QTY_EDIT can't touch since it always requires keeping >=1.
    if (fundingReplaceIndexes.includes(index) || splitIndexes.includes(index)) {
      return sum + (item.quantity || 0) * grossUnitPrice(item);
    }
    return sum;
  }, 0);

  const replacementNewLines = fundingReplaceIndexes.map((index) => ({
    key: `replace-${index}`,
    product: lineEdits[index].product,
    qty: items[index].quantity || 1,
    replacesName: items[index].name,
  }));
  // A Split line's current product is re-added as a substitute at its qty.
  const splitNewLines = splitIndexes.map((index) => ({
    key: `split-${index}`,
    product: { mProductId: items[index].productId, productName: items[index].name },
    qty: items[index].quantity || 1,
  }));
  const allNewLines = [...replacementNewLines, ...splitNewLines, ...newLines];

  // Price is always auto-derived, mirroring the backend: with no price stated
  // it gives every substitute line an equal share of the freed amount (per
  // line, not per unit), so a line's unit price is that share over its qty.
  const perLineShare = allNewLines.length > 0 ? totalFreed / allNewLines.length : 0;
  const resolvedNewLines = allNewLines.map((nl) => ({ ...nl, resolvedUnitPrice: nl.qty > 0 ? perLineShare / nl.qty : 0 }));
  const totalAllocated = resolvedNewLines.reduce((sum, nl) => sum + (nl.qty || 0) * nl.resolvedUnitPrice, 0);
  const balance = totalFreed - totalAllocated;

  const editCount = Object.keys(effectiveEdits).length;
  const fundingActive = hasFundingEdit || hasNewLines;
  // A substitute with no funding edit anywhere in the order can never be
  // funded -- totalFreed and totalAllocated both sit at 0, which would
  // otherwise satisfy the tolerance check and paint this "balanced" (green)
  // right next to the saveBlockedReason telling the operator it's blocked.
  // Treat that specific state as unbalanced so the two messages agree.
  const balanced = !(hasNewLines && !hasFundingEdit) && Math.abs(balance) <= BALANCE_TOLERANCE;

  // A substitute can only ever be funded by an Edit Qty reduction or a
  // Replace on an undiscounted, priced line (which is then sent as REMOVE) --
  // if no line qualifies, Add Substitute Item is a dead end.
  const anyLineFundable = items.some(isFundableLine);

  let saveBlockedReason = null;
  if (editCount === 0 && !hasNewLines) {
    saveBlockedReason = null; // covered by the "select at least one change" message below
  } else if (hasNewLines && !hasFundingEdit) {
    saveBlockedReason = "Reduce a quantity (Edit Qty) or remove a line to fund the substitute item(s) you've added.";
  } else if (hasFundingEdit && !hasNewLines) {
    saveBlockedReason = "Add a substitute item to use the amount freed.";
  } else if (fundingActive && !balanced) {
    saveBlockedReason = `Freed ${formatCurrency(totalFreed)} but allocated ${formatCurrency(totalAllocated)} — adjust quantities or prices to balance.`;
  }

  const handleSaveChanges = () => {
    if (editCount === 0 && !hasNewLines) {
      message.error("Make at least one change before saving.");
      return;
    }
    if (saveBlockedReason) {
      message.error(saveBlockedReason);
      return;
    }
    setView("confirm");
  };

  // REPLACE never changes price: the backend swaps only m_product_id and
  // keeps qty/price/discount/tax exactly as the aggregator sent them (see
  // AggregatorItemModifyService's class doc — "the customer has already been
  // charged by the platform and the platform settles on those amounts"), so
  // a substitute's own catalogue price is never charged. Only QTY_EDIT +
  // newLines can move money, and that nets to ~0 by construction when balanced.
  const originalTotal = order?.amount || 0;
  const netDifference = totalAllocated - totalFreed;
  const amendedTotal = originalTotal + netDifference;

  const handleConfirm = () => {
    // changeType is omitted for REPLACE, not sent as "REPLACE" — the schema's
    // own doc says REPLACE is "the default when omitted, so existing callers
    // are unaffected." Keeping this the exact shape it's always been (just
    // sOrderlineId + newProductId) means a REPLACE-only save is byte-for-byte
    // what this modal sent before TSK-4127 existed.
    const lineChanges = Object.entries(effectiveEdits).map(([index, edit]) => {
      const item = items[index];
      if (edit.type === "QTY_EDIT") {
        return { sOrderlineId: item.sOrderlineID, changeType: "QTY_EDIT", newQty: edit.newQty };
      }
      // A Replace that's funding substitutes (or a Split) goes out as REMOVE
      // (which takes no newQty and can't carry newProductId -- backend
      // guard); its product is re-added as an entry of newLinesPayload below.
      if (fundingReplaceIndexes.includes(index) || edit.type === "SPLIT") {
        return { sOrderlineId: item.sOrderlineID, changeType: "REMOVE" };
      }
      return { sOrderlineId: item.sOrderlineID, newProductId: edit.product.mProductId };
    });
    const newLinesPayload = allNewLines.map((nl) => ({ mProductId: nl.product.mProductId, qty: nl.qty }));
    onConfirm(lineChanges, newLinesPayload);
  };

  return (
    <Modal
      open={visible}
      title={view === "replace" ? `Modify Order Items — #${order?.orderNo || ""}` : "Confirm Order Modification"}
      onCancel={onClose}
      width={520}
      footer={
        view === "replace"
          ? [
              <Button key="cancel" onClick={onClose} disabled={confirming}>
                Cancel
              </Button>,
              <Button key="save" type="primary" style={{ background: "#2F3856", borderColor: "#2F3856", color: "#fff" }} onClick={handleSaveChanges}>
                Save Changes
              </Button>,
            ]
          : [
              <Button key="back" onClick={() => setView("replace")} disabled={confirming}>
                Back
              </Button>,
              <Button key="confirm" type="primary" style={{ background: "#008000", borderColor: "#008000" }} onClick={handleConfirm} loading={confirming}>
                Confirm &amp; Update Order
              </Button>,
            ]
      }
    >
      {view === "replace" ? (
        <>
          <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 10 }}>
            Replace an item or reduce its quantity — then add substitute items to use the freed amount.
          </div>

          {items.map((item, index) => {
            // TSK-3793: with allowModificationInAggregator on, EVERY line is
            // replaceable, not just out-of-stock ones. `flaggedOos` now only
            // drives the visual treatment (red card + "Out of Stock" tag),
            // never whether the Replace button appears.
            // BUG-2163: a line already substituted in a prior confirm stays
            // flagged so its styling/eligibility are unchanged even once its
            // new product itself reports back as in stock.
            const flaggedOos = isItemOutOfStock(item, stockMap) || !!substitutedLineIds?.has(item.sOrderlineID);
            const partial = !flaggedOos && isItemPartialStock(item, stockMap);
            const edit = effectiveEdits[index];
            const isReplacing = activeIndex === index;
            const isEditingQty = qtyEditIndex === index;
            const stockEntry = stockMap?.[item.sOrderlineID];
            const availableQty = stockEntry?.sellableQty ?? stockEntry?.onhandQty;
            const oldQty = item.quantity || 0;
            // Backend floor: "the reduced quantity must be at least 1 -- use
            // Replace to substitute the whole line" (AggregatorItemModifyService).
            // Below 1 in stock, no newQty can ever satisfy that, so Edit Qty
            // would just be an action that's guaranteed to fail.
            const canEditQty = !edit && oldQty > 1 && Number(item.discount || 0) === 0 && (availableQty == null || availableQty >= 1);
            // TIC-10595: with a single replaced line, "+ Add Substitute Item"
            // splits it automatically, so the Split button only appears when
            // there are several to choose between. Every other line keeps
            // exactly the actions it had.
            const canSplit = !edit && splitCandidateCount > 1 && isSplitCandidate(item);

            const cardBorder = flaggedOos ? "#FECACA" : partial ? "#FDE68A" : "#e5e7eb";
            const cardBg = flaggedOos ? "#FEF2F2" : partial ? "#FFFBEB" : "#fff";
            const stockLabel = flaggedOos ? "Out of Stock" : partial ? "Partial Stock" : "In Stock";
            const stockColor = flaggedOos ? "#DC2626" : partial ? "#B45309" : "#16A34A";
            const stockBg = flaggedOos ? "#FEE2E2" : partial ? "#FEF3C7" : "#DCFCE7";

            return (
              <div
                key={index}
                style={{
                  padding: "8px 10px",
                  border: `1px solid ${cardBorder}`,
                  background: cardBg,
                  borderRadius: 6,
                  marginBottom: 6,
                  fontSize: 12,
                }}
              >
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <div>
                    <div
                      style={{
                        fontWeight: 500,
                        color: !edit && flaggedOos ? "#DC2626" : "#111",
                        textDecoration: edit || isReplacing ? "line-through" : "none",
                      }}
                    >
                      {item.name} <span style={{ color: "#6b7280", fontWeight: 400 }}>x{item.quantity}</span>
                    </div>
                    {!edit && !isReplacing && !isEditingQty && (
                      <div style={{ fontSize: 10, color: "#6b7280" }}>
                        {formatCurrency(item.price)} each
                        {flaggedOos || partial ? (
                          <>
                            {" "}
                            · <span style={{ color: stockColor }}>Stock: {availableQty ?? 0}</span>
                          </>
                        ) : (
                          availableQty != null && (
                            <>
                              {" "}
                              · <span style={{ color: "#16A34A" }}>Stock: {availableQty}</span>
                            </>
                          )
                        )}
                      </div>
                    )}
                  </div>
                  {!edit && !isReplacing && !isEditingQty && (
                    <div style={{ display: "flex", gap: 6 }}>
                      {canEditQty && (
                        <Button size="small" onClick={() => handleEditQtyClick(index)} style={{ fontSize: 10, height: 24 }}>
                          Edit Qty
                        </Button>
                      )}
                      {canSplit && (
                        <Button size="small" onClick={() => handleSplitClick(index)} style={{ fontSize: 10, height: 24 }}>
                          Split
                        </Button>
                      )}
                      <Button size="small" onClick={() => handleReplaceClick(index)} style={{ background: "#2F3856", color: "#fff", fontSize: 10, height: 24, border: "none" }}>
                        Replace
                      </Button>
                    </div>
                  )}
                  {(isReplacing || isEditingQty) && (
                    <Tag style={{ fontSize: 8, fontWeight: 700, background: stockBg, color: stockColor, border: `1px solid ${stockColor}33` }}>{stockLabel}</Tag>
                  )}
                </div>

                {isReplacing && (
                  <div style={{ marginTop: 8 }}>
                    <input
                      autoFocus
                      value={searchValue}
                      onChange={(e) => handleSearchChange(e.target.value)}
                      placeholder="Search from UrbanPiper catalogue..."
                      style={{ width: "100%", height: 32, border: "1px solid #d1d5db", borderRadius: 6, padding: "0 10px", fontSize: 12, marginBottom: 6 }}
                    />
                    <div style={{ border: "1px solid #e5e7eb", borderRadius: 6, overflow: "hidden" }}>
                      {searching && <div style={{ padding: 8, fontSize: 11, color: "#6b7280" }}>Searching...</div>}
                      {(() => {
                        // Exclude the item being replaced from its own substitute list — it's
                        // on the catalogue (that's why it matched the search) but offering it
                        // as a "replacement" for itself is meaningless.
                        const candidates = searchResults.filter((product) => product.mProductId !== item.productId);
                        if (searching) return null;
                        if (searchValue.trim().length >= 2 && candidates.length === 0) {
                          return <Empty description="No matches" image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ padding: 8 }} />;
                        }
                        return candidates.map((product) => (
                          <div
                            key={product.cwrPcataloguelinesId || product.mProductId}
                            onClick={() => handleSelectResult(index, product)}
                            style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 8px", cursor: "pointer", fontSize: 11 }}
                          >
                            <div>
                              <div style={{ fontWeight: 500, color: "#111" }}>{product.productName}</div>
                              <div style={{ fontSize: 10, color: "#6b7280" }}>
                                {formatCurrency(product.sellingPrice)} · SKU: {product.productCode}
                              </div>
                            </div>
                            <span style={{ fontSize: 10, color: product.availabilityStatus === "Yes" ? "#16A34A" : "#E99013" }}>
                              {product.sellableQty != null ? `Stock: ${product.sellableQty}` : ""}
                            </span>
                          </div>
                        ));
                      })()}
                    </div>
                    <Button type="link" size="small" onClick={() => setActiveIndex(null)} style={{ fontSize: 10, padding: "6px 0 0" }}>
                      Cancel
                    </Button>
                  </div>
                )}

                {isEditingQty && (
                  <div style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    <InputNumber min={1} max={Math.max(1, oldQty - 1)} value={qtyDraft} onChange={setQtyDraft} size="small" style={{ width: 70 }} />
                    <span style={{ fontSize: 10, color: "#6b7280" }}>of {oldQty} ordered</span>
                    <span style={{ fontSize: 10, color: "#16A34A", fontWeight: 500 }}>
                      Frees {formatCurrency(Math.max(0, oldQty - (Number(qtyDraft) || 0)) * grossUnitPrice(item))}
                    </span>
                    <Button size="small" type="primary" style={{ background: "#2F3856", borderColor: "#2F3856" }} onClick={() => handleApplyQtyEdit(index)}>
                      Apply
                    </Button>
                    <Button size="small" type="text" onClick={() => setQtyEditIndex(null)}>
                      Cancel
                    </Button>
                  </div>
                )}

                {edit && edit.type === "REPLACE" && (
                  <div style={{ background: "#F0FDF4", border: "1px solid #BBF7D0", borderRadius: 6, padding: "10px 12px", marginTop: 8 }}>
                    <div style={{ fontSize: 10, fontWeight: 600, color: "#166534", marginBottom: 4 }}>Replacement Preview</div>
                    <div style={{ color: "#DC2626", fontSize: 11, textDecoration: "line-through" }}>
                      {item.name} · {formatCurrency(item.price)}
                    </div>
                    <div style={{ textAlign: "center", fontSize: 16, color: "#16A34A", margin: "4px 0" }}>↓</div>
                    <div style={{ color: "#16A34A", fontWeight: 600, fontSize: 11 }}>
                      {edit.product.productName} · {fundingReplaceIndexes.includes(String(index)) ? `Qty: ${oldQty}` : formatCurrency(item.price)}
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, marginTop: 6, paddingTop: 6, borderTop: "1px dashed #BBF7D0" }}>
                      {fundingReplaceIndexes.includes(String(index)) ? (
                        <>
                          <span style={{ color: "#6b7280" }}>Shared with substitute items</span>
                          <span style={{ color: "#16A34A", fontWeight: 600 }}>{formatCurrency(oldQty * grossUnitPrice(item))}</span>
                        </>
                      ) : (
                        <>
                          <span style={{ color: "#6b7280" }}>Price</span>
                          <span style={{ color: "#16A34A", fontWeight: 600 }}>No change — order total stays the same</span>
                        </>
                      )}
                    </div>
                    <Button type="link" size="small" onClick={() => handleChangeEdit(index)} style={{ fontSize: 10, padding: "4px 0 0" }}>
                      Change
                    </Button>
                  </div>
                )}

                {edit && edit.type === "SPLIT" && (
                  <div style={{ background: "#F0FDF4", border: "1px solid #BBF7D0", borderRadius: 6, padding: "10px 12px", marginTop: 8 }}>
                    <div style={{ fontSize: 10, fontWeight: 600, color: "#166534", marginBottom: 4 }}>Split Line</div>
                    <div style={{ fontSize: 11, color: "#111" }}>
                      Kept as a substitute · Qty: {oldQty}
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, marginTop: 6, paddingTop: 6, borderTop: "1px dashed #BBF7D0" }}>
                      <span style={{ color: "#6b7280" }}>Shared with substitute items</span>
                      <span style={{ color: "#16A34A", fontWeight: 600 }}>{formatCurrency(oldQty * grossUnitPrice(item))}</span>
                    </div>
                    {edit.auto ? (
                      <div style={{ fontSize: 10, color: "#6b7280", paddingTop: 4 }}>Applied automatically — remove the substitute item(s) to undo.</div>
                    ) : (
                      <Button type="link" size="small" onClick={() => handleChangeEdit(index)} style={{ fontSize: 10, padding: "4px 0 0" }}>
                        Change
                      </Button>
                    )}
                  </div>
                )}

                {edit && edit.type === "QTY_EDIT" && (
                  <div style={{ background: "#FFFBEB", border: "1px solid #FDE68A", borderRadius: 6, padding: "10px 12px", marginTop: 8 }}>
                    <div style={{ fontSize: 10, fontWeight: 600, color: "#92400E", marginBottom: 4 }}>Quantity Reduced</div>
                    <div style={{ fontSize: 11, color: "#111" }}>
                      {item.name} · <span style={{ textDecoration: "line-through", color: "#6b7280" }}>{oldQty}</span> → <strong>{edit.newQty}</strong>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, marginTop: 6, paddingTop: 6, borderTop: "1px dashed #FDE68A" }}>
                      <span style={{ color: "#6b7280" }}>Amount freed</span>
                      <span style={{ color: "#B45309", fontWeight: 600 }}>{formatCurrency((oldQty - edit.newQty) * grossUnitPrice(item))}</span>
                    </div>
                    <Button type="link" size="small" onClick={() => handleChangeEdit(index)} style={{ fontSize: 10, padding: "4px 0 0" }}>
                      Change
                    </Button>
                  </div>
                )}
              </div>
            );
          })}

          <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid #e5e7eb" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: "#111" }}>Substitute Items</div>
              {!addingNewLine && anyLineFundable && (
                <Button size="small" onClick={handleAddSubstituteClick} style={{ fontSize: 10, height: 24 }}>
                  + Add Substitute Item
                </Button>
              )}
            </div>
            {!anyLineFundable && !hasNewLines && (
              <div style={{ fontSize: 10, color: "#6b7280", marginBottom: 4 }}>
                No line in this order can be reduced in quantity, so there's nothing to fund a substitute with — use Replace instead.
              </div>
            )}

            {newLines.map((nl) => {
              const resolved = resolvedNewLines.find((r) => r.key === nl.key);
              return (
                <div
                  key={nl.key}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    background: "#F0FDF4",
                    border: "1px solid #BBF7D0",
                    borderRadius: 6,
                    padding: "8px 10px",
                    marginBottom: 6,
                    fontSize: 11,
                  }}
                >
                  <div>
                    <div style={{ fontWeight: 500, color: "#111" }}>
                      {nl.product.productName} <span style={{ color: "#6b7280", fontWeight: 400 }}>x{nl.qty}</span>
                    </div>
                    <div style={{ fontSize: 10, color: "#6b7280" }}>{formatCurrency(resolved?.resolvedUnitPrice || 0)} each (auto)</div>
                  </div>
                  <Button type="link" size="small" danger onClick={() => handleRemoveNewLine(nl.key)} style={{ fontSize: 10 }}>
                    Remove
                  </Button>
                </div>
              );
            })}

            {addingNewLine && (
              <div style={{ border: "1px solid #e5e7eb", borderRadius: 6, padding: 8 }}>
                {!pendingNewLineProduct ? (
                  <>
                    <input
                      autoFocus
                      value={newLineSearchValue}
                      onChange={(e) => handleNewLineSearchChange(e.target.value)}
                      placeholder="Search from UrbanPiper catalogue..."
                      style={{ width: "100%", height: 32, border: "1px solid #d1d5db", borderRadius: 6, padding: "0 10px", fontSize: 12, marginBottom: 6 }}
                    />
                    <div style={{ border: "1px solid #e5e7eb", borderRadius: 6, overflow: "hidden" }}>
                      {newLineSearching && <div style={{ padding: 8, fontSize: 11, color: "#6b7280" }}>Searching...</div>}
                      {!newLineSearching && newLineSearchValue.trim().length >= 2 && newLineSearchResults.length === 0 && (
                        <Empty description="No matches" image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ padding: 8 }} />
                      )}
                      {!newLineSearching &&
                        newLineSearchResults.map((product) => (
                          <div
                            key={product.cwrPcataloguelinesId || product.mProductId}
                            onClick={() => handlePickNewLineProduct(product)}
                            style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 8px", cursor: "pointer", fontSize: 11 }}
                          >
                            <div>
                              <div style={{ fontWeight: 500, color: "#111" }}>{product.productName}</div>
                              <div style={{ fontSize: 10, color: "#6b7280" }}>
                                {formatCurrency(product.sellingPrice)} · SKU: {product.productCode}
                              </div>
                            </div>
                            <span style={{ fontSize: 10, color: product.availabilityStatus === "Yes" ? "#16A34A" : "#E99013" }}>
                              {product.sellableQty != null ? `Stock: ${product.sellableQty}` : ""}
                            </span>
                          </div>
                        ))}
                    </div>
                    <Button type="link" size="small" onClick={() => setAddingNewLine(false)} style={{ fontSize: 10, padding: "6px 0 0" }}>
                      Cancel
                    </Button>
                  </>
                ) : (
                  <>
                    <div style={{ fontWeight: 500, fontSize: 12, marginBottom: 6 }}>{pendingNewLineProduct.productName}</div>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                      <div>
                        <div style={{ fontSize: 10, color: "#6b7280" }}>Qty</div>
                        <InputNumber min={1} value={pendingNewLineQty} onChange={setPendingNewLineQty} size="small" style={{ width: 70 }} />
                      </div>
                      <Button size="small" type="primary" style={{ background: "#2F3856", borderColor: "#2F3856" }} onClick={handleConfirmAddNewLine}>
                        Add
                      </Button>
                      <Button size="small" type="text" onClick={() => setPendingNewLineProduct(null)}>
                        Back
                      </Button>
                    </div>
                  </>
                )}
              </div>
            )}

            {fundingActive && (
              <div
                style={{
                  marginTop: 10,
                  padding: "8px 10px",
                  borderRadius: 6,
                  fontSize: 11,
                  background: balanced ? "#F0FDF4" : "#FEF2F2",
                  border: `1px solid ${balanced ? "#BBF7D0" : "#FECACA"}`,
                  color: balanced ? "#166534" : "#DC2626",
                }}
              >
                Freed {formatCurrency(totalFreed)} · Allocated {formatCurrency(totalAllocated)} · Balance {formatCurrency(balance)}
              </div>
            )}
            {saveBlockedReason && (hasFundingEdit || hasNewLines) && (
              <div style={{ marginTop: 6, fontSize: 10, color: "#DC2626" }}>{saveBlockedReason}</div>
            )}
          </div>
        </>
      ) : (
        <>
          <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 12 }}>Review the changes below. Once confirmed, the order will be updated immediately.</div>

          <div style={{ fontSize: 12, fontWeight: 600, color: "#111", marginBottom: 6 }}>Changes</div>
          {Object.entries(effectiveEdits).map(([index, edit]) => {
            const item = items[index];
            if (edit.type === "SPLIT") {
              return (
                <div key={index} style={{ background: "#F0FDF4", border: "1px solid #BBF7D0", borderRadius: 6, padding: "10px 12px", marginBottom: 12 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                    <span style={{ fontSize: 10, fontWeight: 600, color: "#166534" }}>Line {item.line || Number(index) + 1} — Split</span>
                    <Tag style={{ background: "#DCFCE7", color: "#16A34A", border: "1px solid #BBF7D0", fontSize: 8 }}>MODIFIED</Tag>
                  </div>
                  <div style={{ fontSize: 11, color: "#111" }}>
                    {item.name} · Qty: {item.quantity}
                  </div>
                  <div style={{ fontSize: 11, color: "#166534", marginTop: 4 }}>Frees {formatCurrency(item.quantity * grossUnitPrice(item))}, shared with the substitute items below</div>
                </div>
              );
            }
            if (edit.type === "REPLACE" && fundingReplaceIndexes.includes(index)) {
              // Sent as REMOVE + the replacement listed under "Substitute
              // Items Added" below -- show it as a swap without a stale price.
              return (
                <div key={index} style={{ background: "#F0FDF4", border: "1px solid #BBF7D0", borderRadius: 6, padding: "10px 12px", marginBottom: 12 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                    <span style={{ fontSize: 10, fontWeight: 600, color: "#166534" }}>Line {item.line || Number(index) + 1} — Product Substitution</span>
                    <Tag style={{ background: "#DCFCE7", color: "#16A34A", border: "1px solid #BBF7D0", fontSize: 8 }}>MODIFIED</Tag>
                  </div>
                  <div style={{ color: "#DC2626", fontSize: 11, textDecoration: "line-through" }}>
                    {item.name} · Qty: {item.quantity}
                  </div>
                  <div style={{ textAlign: "center", fontSize: 16, color: "#16A34A", margin: "4px 0" }}>↓</div>
                  <div style={{ color: "#16A34A", fontWeight: 600, fontSize: 11 }}>
                    {edit.product.productName} · Qty: {item.quantity}
                  </div>
                  <div style={{ fontSize: 11, color: "#166534", marginTop: 4 }}>Frees {formatCurrency(item.quantity * grossUnitPrice(item))}, shared with the substitute items below</div>
                </div>
              );
            }
            if (edit.type === "REPLACE") {
              return (
                <div key={index} style={{ background: "#F0FDF4", border: "1px solid #BBF7D0", borderRadius: 6, padding: "10px 12px", marginBottom: 12 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                    <span style={{ fontSize: 10, fontWeight: 600, color: "#166534" }}>Line {item.line || Number(index) + 1} — Product Substitution</span>
                    <Tag style={{ background: "#DCFCE7", color: "#16A34A", border: "1px solid #BBF7D0", fontSize: 8 }}>MODIFIED</Tag>
                  </div>
                  <div style={{ color: "#DC2626", fontSize: 11, textDecoration: "line-through" }}>
                    {item.name} · Qty: {item.quantity} · {formatCurrency(item.price)}
                  </div>
                  <div style={{ textAlign: "center", fontSize: 16, color: "#16A34A", margin: "4px 0" }}>↓</div>
                  <div style={{ color: "#16A34A", fontWeight: 600, fontSize: 11 }}>
                    {edit.product.productName} · Qty: {item.quantity} · {formatCurrency(item.price)}
                  </div>
                </div>
              );
            }
            return (
              <div key={index} style={{ background: "#FFFBEB", border: "1px solid #FDE68A", borderRadius: 6, padding: "10px 12px", marginBottom: 12 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                  <span style={{ fontSize: 10, fontWeight: 600, color: "#92400E" }}>Line {item.line || Number(index) + 1} — Quantity Reduced</span>
                  <Tag style={{ background: "#FEF3C7", color: "#B45309", border: "1px solid #FDE68A", fontSize: 8 }}>MODIFIED</Tag>
                </div>
                <div style={{ fontSize: 11, color: "#111" }}>
                  {item.name} · <span style={{ textDecoration: "line-through", color: "#6b7280" }}>Qty: {item.quantity}</span> → Qty: {edit.newQty}
                </div>
                <div style={{ fontSize: 11, color: "#B45309", marginTop: 4 }}>Frees {formatCurrency((item.quantity - edit.newQty) * grossUnitPrice(item))}</div>
              </div>
            );
          })}

          {allNewLines.length > 0 && (
            <>
              <div style={{ fontSize: 12, fontWeight: 600, color: "#111", margin: "12px 0 6px" }}>Substitute Items Added</div>
              {resolvedNewLines.map((nl) => (
                <div key={nl.key} style={{ background: "#F0FDF4", border: "1px solid #BBF7D0", borderRadius: 6, padding: "10px 12px", marginBottom: 12 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: "#166534" }}>{nl.product.productName}</div>
                  <div style={{ fontSize: 11, color: "#111" }}>
                    Qty: {nl.qty} · {formatCurrency(nl.resolvedUnitPrice)} each · Total: {formatCurrency(nl.qty * nl.resolvedUnitPrice)}
                  </div>
                </div>
              ))}
            </>
          )}

          <div style={{ fontSize: 12, fontWeight: 600, color: "#111", marginBottom: 6 }}>Financial Impact</div>
          <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8, padding: "14px 16px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 4 }}>
              <span style={{ color: "#6b7280" }}>Original Total</span>
              <span style={{ fontWeight: 500, color: "#111" }}>{formatCurrency(originalTotal)}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 4 }}>
              <span style={{ color: "#6b7280" }}>Amended Total</span>
              <span style={{ fontWeight: 500, color: "#111" }}>{formatCurrency(amendedTotal)}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, borderTop: "1px solid #e5e7eb", paddingTop: 6, marginTop: 4 }}>
              <span style={{ color: "#6b7280" }}>
                <strong>Net Difference</strong>
              </span>
              <span style={{ fontWeight: 500, color: netDifference > 0 ? "#16A34A" : netDifference < 0 ? "#DC2626" : "#111" }}>
                <strong>{formatCurrency(netDifference)}</strong>
              </span>
            </div>
          </div>

          <div style={{ background: "#F0FDF4", border: "1px solid #BBF7D0", borderRadius: 6, padding: "8px 10px", marginTop: 12, fontSize: 11, color: "#166534" }}>
            Order records will be updated immediately. The modified items will appear on the sales invoice when the order is dispatched.
          </div>
        </>
      )}
    </Modal>
  );
};

export default ModifyItemsModal;
